import React from "react";
import { useField } from "formik";
import { Form } from "react-bootstrap";

interface InputDoubleNumberProps {
  name: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  min?: number;
  max?: number;
  required?: boolean;
}

const InputDoubleNumber: React.FC<InputDoubleNumberProps> = ({
  name,
  placeholder = "",
  disabled = false,
  className = "",
  min,
  max,
  required,
}) => {
  const [field, meta] = useField(name);

  let formGroupClass = "";
  if (meta && meta.touched) {
    if (meta.error) {
      formGroupClass = "is-invalid";
    } else {
      formGroupClass = "is-valid";
    }
  }

  const checkIfNumber = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    /**
     * Allowing: Integers, Decimal Point (.), Backspace, Tab, Delete, Left & Right arrow keys
     * Disallowing: Commas (,), multiple decimal points
     **/

    // Allow Backspace, Tab, Delete, ArrowLeft, ArrowRight
    if (
      event.key === "Backspace" ||
      event.key === "Tab" ||
      event.key === "Delete" ||
      event.key === "ArrowLeft" ||
      event.key === "ArrowRight"
    ) {
      return;
    }

    // Check if the input already has a decimal point
    const hasDecimalPoint = field.value && field.value.includes(".");

    // This regex allows:
    // - An optional negative sign (-)
    // - Any number of digits before the decimal point (\d*)
    // - An optional single decimal point (\.?), followed by any number of digits after the decimal point (\d*)
    // Comma (,) is explicitly disallowed by not including it in the regex
    const regex = new RegExp(/^-?\d*\.?\d*$/);

    // If the key pressed doesn't match the regex, or it's a comma, or another decimal point is entered, prevent the default behavior
    if (
      !event.key.match(regex) ||
      event.key === "," ||
      (event.key === "." && hasDecimalPoint)
    ) {
      event.preventDefault();
    }
  };

  return (
    <>
      <Form.Control
        className={`${className} ${formGroupClass}`}
        type={"text"}
        placeholder={placeholder}
        disabled={disabled}
        {...field}
        value={field.value || (field.value === 0 ? 0 : "")}
        min={min}
        max={max}
        required={required}
        onKeyDown={checkIfNumber}
      />
      <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
    </>
  );
};

export default InputDoubleNumber;